import { MetaRatingDelta } from "@/components/MetaRatingDelta";
import Tooltip from "@/components/atoms/Tooltip/Tooltip";
import { GAME_ASSET_SLUG } from "@/config/game";
import { cn } from "@/lib/utils";
import { PlayStylesLabRecommendation } from "@/redux/playStylesLab/playStylesLabSlice";
import { BREAKPOINTS } from "@/settings";
import { RefreshCcw } from "lucide-react";
import { memo } from "react";
import { useMediaQuery } from "usehooks-ts";

type Props = {
  item: PlayStylesLabRecommendation;
  name: string;
  selected?: boolean;
  disabled?: boolean;
  // Same id is applied in the other list — clicking swaps it over.
  crossApplied?: boolean;
  onSelect?: (item: PlayStylesLabRecommendation) => void;
};

export const PlayStyleIcon = ({
  id,
  plus,
  className,
}: {
  id: number;
  plus: boolean;
  className?: string;
}) => {
  const alt = `playstyle-${plus ? "plus-" : ""}${id}`;
  return (
    <img
      className={cn("size-6 shrink-0", className)}
      alt={alt}
      src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/${GAME_ASSET_SLUG}/${plus ? "playstylesplus" : "playstyles"}/${id}.png`}
    />
  );
};

export const PlayStyleCard = memo(function PlayStyleCard({
  item,
  name,
  selected,
  disabled,
  crossApplied,
  onSelect,
}: Props) {
  const plus = item.playStylesPlus.length > 0;
  const id = plus ? item.playStylesPlus[0] : item.playStyles[0];
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);

  const swapTooltip = plus
    ? `Replaces base ${name} with ${name}+`
    : `Replaces ${name}+ with base ${name}`;

  return (
    <button
      onClick={() => onSelect?.(item)}
      disabled={disabled}
      className={cn(
        "flex items-center justify-between gap-x-2 w-full px-4 py-2 rounded-lg",
        "border-2 bg-panel-background shadow-button-new group",
        selected
          ? "border-emerald-500 bg-panel-middleground "
          : "border-transparent hover:bg-panel-middleground",
        disabled && "opacity-50 hover:bg-panel-background cursor-not-allowed",
      )}
    >
      <div className="flex items-center gap-x-2.5 py-1.5 rounded min-w-0">
        <PlayStyleIcon id={id} plus={plus} />
        <span className="font-medium text-sm text-dark-secondary text-left leading-5 truncate">
          {name}
        </span>
        {crossApplied && (
          <Tooltip
            content={swapTooltip}
            disabled={isTouchDevice}
            delayDuration={100}
          >
            <RefreshCcw className="size-4 shrink-0 text-dark-tertiary" />
          </Tooltip>
        )}
      </div>
      <MetaRatingDelta metaRating={undefined} delta={item.metaRating.delta} />
    </button>
  );
});
